import { Description } from 'utils/types'

// A group's description is '' until it has been saved once and a Description
// after that (groups.store writes description: '' on create). Every read below
// already relies on a field off that empty string coming back undefined, and on
// spreading it giving {}, so this hands back exactly that rather than changing
// what any of them do.
export const asDescription = (description: Description | string | undefined | null): Partial<Description> =>
  typeof description === 'object' && description !== null ? description : {}

// What a group's description should be once it is saved. addGroup starts it at
// '' and the manager only replaces it by picking an icon or a category, so a
// group saved without doing that kept an empty string. It gets the group's name
// instead.
//
// This changes nothing about how the group looks: every reader asks the
// description for .color, .icon or .iconColor, and a string answers undefined
// to all of them exactly as '' did, so the same fallbacks run. Only a
// description that is still empty is filled in - one the manager picked is an
// object and is handed straight back.
export const defaultDescriptionFor = (
  group: { name?: string | null; description?: Description | string | null },
): Description | string | null | undefined =>
  !group.description && !isUnnamedGroup(group.name) ? group.name : group.description

// What a group is called before anyone names it. A space rather than '' so the
// field holds the default rather than nothing, while still reading as empty.
export const DEFAULT_GROUP_NAME = ' '

// A single character is not a name anyone can find a group by.
export const MIN_GROUP_NAME_LENGTH = 2

// A group nobody has named yet: too short, or still carrying the default.
// disabledSend uses this to keep the save button off, and the description
// default uses it so a blank name is never copied across. Measured after
// trimming, so a name typed as spaces counts as unnamed and ' א ' counts as one
// character rather than three.
//
// The default is checked on its own as well as by length: it reads as empty
// today, but it has not always, and the guard has to hold either way.
export const isUnnamedGroup = (name?: string | null): boolean => {
  const trimmed = (name ?? '').trim()
  return trimmed.length < MIN_GROUP_NAME_LENGTH || trimmed === DEFAULT_GROUP_NAME.trim()
}
